'use client'

import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import { Button } from '@/components/Button'
import { ArrowRightCircle, Delete } from '@/components/Icons'
import { gtmTrackEvent } from '@/lib/analytics'

const IMG_EXISTING_CUSTOMER = '/img/existing-customer.png'
const IMG_NEW_CUSTOMER = '/img/current-customer.png'

export default function ApplyDialog ({ open, onClose, sourcePageUrl, sourceComponent }) {
  const handleLinkClick = (linkName, linkUrl) => {
    gtmTrackEvent('applyDialogLinkClicked', {
      linkDestination: linkName,
      linkUrl: linkUrl,
      sourcePageUrl: sourcePageUrl || window.location.href,
      sourceComponent: sourceComponent || 'Unknown'
    })
  }

  return (
    <Dialog open={open} onClose={onClose} className='relative z-50'>
      <DialogBackdrop
        transition
        className='fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in'
      />

      <div className='fixed inset-0 z-10 w-screen overflow-y-auto'>
        <div className='flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-6'>
          <DialogPanel
            transition
            className='relative w-full max-w-lg transform overflow-hidden rounded-2xl border border-default-200 bg-default-50 px-4 pb-5 pt-4 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:max-w-[480px] sm:px-5 sm:pb-8 sm:pt-5 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95'
          >
            <button
              type='button'
              onClick={onClose}
              className='absolute right-3 top-3 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-500 text-white shadow-[0_1px_3px_rgba(0,0,0,0.1)] transition-colors hover:bg-primary-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2'
              aria-label='Close dialog'
            >
              <Delete className='h-4 w-4' />
            </button>

            <div className='flex flex-col gap-3 sm:gap-6'>
              <div className='flex flex-col gap-1.5 sm:gap-2'>
                <div className='flex flex-col items-center text-center'>
                  <DialogTitle
                    as='h2'
                    className='text-lg font-extrabold leading-6 tracking-[-0.36px] text-black sm:text-[22px] sm:leading-7 sm:tracking-[-0.44px]'
                  >
                    Apply now
                  </DialogTitle>
                </div>
              </div>

              <div className='flex gap-3 pt-4 sm:gap-5 sm:pt-0'>
                <div className='relative h-20 w-20 shrink-0 overflow-hidden rounded sm:h-[104px] sm:w-[104px]'>
                  <img
                    src={IMG_EXISTING_CUSTOMER}
                    alt=''
                    className='h-full w-full object-cover'
                    width={104}
                    height={104}
                  />
                </div>
                <div className='flex min-w-0 flex-1 flex-col gap-1.5 sm:gap-2'>
                  <p className='text-sm font-extrabold leading-5 tracking-[-0.14px] text-default-900 sm:text-base sm:leading-[22px] sm:tracking-[-0.16px]'>
                    Existing customer
                  </p>
                  <p className='text-[11px] font-bold leading-4 text-default-500 sm:text-xs sm:leading-[18px] sm:text-default-500'>
                    Apply faster by signing into myUDC.
                  </p>
                  <Button
                    href='https://my.udc.co.nz/logon'
                    target='_blank'
                    size='sm'
                    variant='primary'
                    mobile='block'
                    className='mt-0.5 w-full justify-center border border-white/30 shadow-[0_1px_3px_rgba(0,0,0,0.1),0_1px_2px_rgba(0,0,0,0.06)]'
                    onClick={() => handleLinkClick('myUDC', 'https://my.udc.co.nz/logon')}
                  >
                    <span className='relative block pr-5'>
                      Sign into myUDC{' '}
                      <ArrowRightCircle className='absolute right-0 top-1/2 -mt-2' />
                    </span>
                  </Button>
                </div>
              </div>

              <div className='h-px w-full bg-primary-200' aria-hidden='true' />

              <div className='flex gap-3 sm:gap-5'>
                <div className='relative h-20 w-20 shrink-0 overflow-hidden rounded sm:h-[104px] sm:w-[104px]'>
                  <img
                    src={IMG_NEW_CUSTOMER}
                    alt=''
                    className='h-full w-full object-cover'
                    width={104}
                    height={104}
                  />
                </div>
                <div className='flex min-w-0 flex-1 flex-col gap-1.5 sm:gap-2'>
                  <p className='text-sm font-extrabold leading-5 tracking-[-0.14px] text-default-900 sm:text-base sm:leading-[22px] sm:tracking-[-0.16px]'>
                    New to UDC Finance
                  </p>
                  <p className='text-[11px] font-bold leading-4 text-default-500 sm:text-xs sm:leading-[18px] sm:text-default-900'>
                    Start your new application online and follow the guided steps.
                  </p>
                  <Button
                    href='https://online.udc.co.nz/'
                    target='_blank'
                    size='sm'
                    variant='secondary'
                    mobile='block'
                    className='mt-0.5 w-full justify-center border border-white/30 shadow-[0_1px_3px_rgba(0,0,0,0.1),0_1px_2px_rgba(0,0,0,0.06)]'
                    aria-label='Continue to online application'
                    onClick={() => handleLinkClick('Online Application', 'https://online.udc.co.nz/')}
                  >
                    <span className='relative block pr-5'>
                      Start application{' '}
                      <ArrowRightCircle className='absolute right-0 top-1/2 -mt-2' />
                    </span>
                  </Button>
                </div>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  )
}
